import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { Language, translations, languages, loadTranslations } from './translations';
import { siteConfig } from '@/config/site';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language, updateUrl?: boolean) => void;
  t: (key: string) => string;
  languages: typeof languages;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * Detect language from URL path
 */
function detectLanguageFromUrl(): Language {
  const path = window.location.pathname;
  const langMatch = path.match(/^\/(de|es|fr|pt|ru|it|sv|zh)(\/|$)/);
  if (langMatch) {
    return langMatch[1] as Language;
  }
  return siteConfig.defaultLanguage;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const urlLang = detectLanguageFromUrl();
    if (urlLang !== siteConfig.defaultLanguage) {
      return urlLang;
    }
    return siteConfig.defaultLanguage;
  });

  // Loaded translation strings for the current language
  const [currentStrings, setCurrentStrings] = useState<Record<string, string>>(translations.en);
  const loadingRef = useRef<Language | null>(null);

  // Load translations when language changes
  useEffect(() => {
    if (language === 'en') {
      setCurrentStrings(translations.en);
      return;
    }
    loadingRef.current = language;
    loadTranslations(language).then(strings => {
      // Only apply if this is still the current language
      if (loadingRef.current === language) {
        setCurrentStrings(strings);
      }
    });
  }, [language]);

  // Listen for popstate (browser back/forward) to sync language
  useEffect(() => {
    const handlePopState = () => {
      const urlLang = detectLanguageFromUrl();
      if (urlLang !== language) {
        setLanguageState(urlLang);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [language]);

  const setLanguage = useCallback((lang: Language, updateUrl: boolean = true) => {
    setLanguageState(lang);
    
    if (updateUrl && typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      
      // Remove existing language prefix
      const pathWithoutLang = currentPath.replace(/^\/(de|es|fr|pt|ru|it|sv|zh)/, '') || '/';
      
      // Build new path
      let newPath: string;
      if (lang === siteConfig.defaultLanguage) {
        newPath = pathWithoutLang;
      } else {
        newPath = `/${lang}${pathWithoutLang}`;
      }
      
      // Update URL without page reload
      window.history.pushState({}, '', newPath + currentSearch);
      
      // Dispatch event so LanguageRouter can react if needed
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, []);

  const t = useCallback((key: string): string => {
    return currentStrings[key] || translations.en[key] || key;
  }, [currentStrings]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

const fallbackContext: LanguageContextType = {
  language: siteConfig.defaultLanguage,
  setLanguage: () => {},
  t: (key: string) => translations.en[key] || key,
  languages,
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  // Return fallback during HMR boundary reloads to avoid blank screen
  return context ?? fallbackContext;
};
